import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import {
  Loader2,
  DownloadCloud,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  AlertTriangle,
  UserPlus,
  UserCheck,
  Zap,
  Mail,
} from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const min = Math.floor(seconds / 60)
  const sec = seconds % 60
  if (min < 60) return `${min}min${sec > 0 ? ` ${sec}s` : ''}`
  const hr = Math.floor(min / 60)
  return `${hr}h${min % 60 > 0 ? ` ${min % 60}min` : ''}`
}

export function KlaviyoImportCard() {
  const queryClient = useQueryClient()

  // Poll for import status
  const { data: jobStatus } = useQuery({
    queryKey: ['klaviyo-import-status'],
    queryFn: () => api.settings.getKlaviyoImportStatus(),
    refetchInterval: (query) => {
      const status = query.state.data
      return status?.status === 'running' ? 3000 : false
    },
  })

  const importMutation = useMutation({
    mutationFn: () => api.settings.importKlaviyoProfiles(),
    onSuccess: () => {
      toast.success('Importação iniciada!', {
        description: 'Acompanhe o progresso nesta página.',
      })
      queryClient.invalidateQueries({ queryKey: ['klaviyo-import-status'] })
    },
    onError: (error: Error) => {
      toast.error('Erro ao iniciar importação', {
        description: error.message || 'Tente novamente mais tarde',
      })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => api.settings.cancelKlaviyoImport(jobStatus!.id),
    onSuccess: () => {
      toast.success('Cancelamento solicitado', {
        description: 'A importação será interrompida em instantes.',
      })
      queryClient.invalidateQueries({ queryKey: ['klaviyo-import-status'] })
    },
    onError: (error: Error) => {
      toast.error('Erro ao cancelar', {
        description: error.message,
      })
    },
  })

  const isRunning = jobStatus?.status === 'running'
  const isCompleted = jobStatus?.status === 'completed'
  const isFailed = jobStatus?.status === 'failed'
  const isCancelled = jobStatus?.status === 'cancelled'
  const hasJob = isRunning || isCompleted || isFailed || isCancelled

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center h-9 w-9 shrink-0 rounded-lg bg-primary/10">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base">Importar Perfis</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Crie membros e cupons para toda sua base do Klaviyo.
              </CardDescription>
            </div>
          </div>
          {hasJob && <StatusBadge status={jobStatus!.status} />}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Failed State */}
        {isFailed && (
          <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-md p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <span className="block">A última importação falhou.</span>
              {jobStatus!.errorMessage && (
                <span className="block text-xs opacity-80">{jobStatus!.errorMessage}</span>
              )}
            </div>
          </div>
        )}

        {/* Cancelled State */}
        {isCancelled && (
          <div className="flex items-start gap-2 text-sm text-yellow-600 bg-yellow-50 rounded-md p-3">
            <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <span className="block">Importação cancelada.</span>
              <span className="block text-xs opacity-80">
                Processado até: {jobStatus!.lastProcessedEmail || 'Início'}
              </span>
            </div>
          </div>
        )}

        {/* Progress Section — running or completed */}
        {(isRunning || isCompleted) && (
          <div className="space-y-3">
            {/* Progress Bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  {isRunning ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Importando perfis...
                    </>
                  ) : (
                    'Concluído'
                  )}
                </span>
                {jobStatus!.elapsedSeconds != null && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDuration(jobStatus!.elapsedSeconds)}
                    {isCompleted && ' total'}
                  </span>
                )}
              </div>
              {isRunning ? (
                <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                  {jobStatus!.totalProfiles && jobStatus!.totalProfiles > 0 ? (
                    <Progress
                      value={(jobStatus!.processedCount / jobStatus!.totalProfiles) * 100}
                      className="h-full w-full"
                    />
                  ) : (
                    <div className="h-full w-1/3 bg-primary rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]" />
                  )}
                </div>
              ) : (
                <Progress value={100} className="h-2 [&>div]:bg-green-500" />
              )}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
              <StatItem
                icon={<UserPlus className="h-3.5 w-3.5 text-green-600" />}
                label="Criados"
                value={jobStatus!.createdCount}
              />
              <StatItem
                icon={<UserCheck className="h-3.5 w-3.5 text-blue-600" />}
                label="Já existiam"
                value={jobStatus!.existingCount}
              />
              {jobStatus!.skippedCount > 0 && (
                <StatItem
                  icon={<Mail className="h-3.5 w-3.5 text-muted-foreground" />}
                  label="Sem email"
                  value={jobStatus!.skippedCount}
                />
              )}
              {jobStatus!.failedCount > 0 && (
                <StatItem
                  icon={<XCircle className="h-3.5 w-3.5 text-destructive" />}
                  label="Falhas"
                  value={jobStatus!.failedCount}
                />
              )}
            </div>

            {/* Speed + Last Email (running only) */}
            {isRunning && (
              <div className="space-y-1.5 text-xs text-muted-foreground border-t pt-2.5">
                {jobStatus!.speed != null && jobStatus!.speed > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Zap className="h-3 w-3" />
                    <span>{jobStatus!.speed} perfis/min</span>
                  </div>
                )}
                {jobStatus!.lastProcessedEmail && (
                  <div className="flex items-center gap-1.5 truncate">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="truncate">{jobStatus!.lastProcessedEmail}</span>
                  </div>
                )}
                {jobStatus!.lastError && !jobStatus!.lastProcessedEmail && (
                  <div className="flex items-center gap-1.5 truncate text-blue-600">
                    <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                    <span className="truncate">{jobStatus!.lastError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Summary line (completed) */}
            {isCompleted && (
              <div className="text-xs text-muted-foreground border-t pt-2.5">
                <span className="font-medium text-foreground">
                  {jobStatus!.processedCount.toLocaleString()}
                </span>{' '}
                perfis processados
                {jobStatus!.speed != null && jobStatus!.speed > 0 && (
                  <> · {jobStatus!.speed} perfis/min</>
                )}
              </div>
            )}

            {/* Last error context */}
            {jobStatus!.lastError && jobStatus!.failedCount > 0 && (
              <div className="text-xs text-destructive/80 bg-destructive/5 rounded p-2 line-clamp-2">
                Último erro: {jobStatus!.lastError}
              </div>
            )}
          </div>
        )}

        {/* Action Area */}
        <div className="flex items-center gap-3">
          {isRunning && jobStatus?.id ? (
            <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
              <ChonkyButton variant="secondary" className="w-full sm:flex-1" disabled loading>
                Importando...
              </ChonkyButton>
              <ChonkyButton
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={() => cancelMutation.mutate()}
                loading={cancelMutation.isPending}
              >
                Cancelar
              </ChonkyButton>
              {!jobStatus.totalProfiles && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() =>
                    api.settings.retryProfileCount(jobStatus.id).then(() => {
                      toast.success('Contagem iniciada!')
                    })
                  }
                >
                  Recalcular
                </Button>
              )}
            </div>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  loading={importMutation.isPending}
                >
                  <DownloadCloud className="mr-2 h-4 w-4" />
                  {hasJob ? 'Importar novamente' : 'Importar perfis'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
                <AlertDialogHeader>
                  <AlertDialogTitle>Importar todos os perfis?</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2 text-xs sm:text-sm">
                    <span className="block">
                      Isso irá buscar <strong>todos os perfis</strong> da sua conta Klaviyo e para
                      cada um:
                    </span>
                    <span className="block pl-2 border-l-2 border-muted space-y-1">
                      <span className="block">• Criar membro na Rafobase (se não existir)</span>
                      <span className="block">• Gerar cupom de indicação (Yampi/Guru)</span>
                      <span className="block">
                        • Enviar evento "Joined Referral Program" ao Klaviyo
                      </span>
                    </span>
                    <span className="block text-xs mt-2">
                      Perfis mais recentes são importados primeiro. Você pode fechar o navegador — a
                      importação continua em background.
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => importMutation.mutate()}>
                    Iniciar importação
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function StatItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5 sm:gap-2 rounded-md bg-muted/50 px-2 py-1.5 sm:px-3 sm:py-2">
      {icon}
      <div className="flex items-baseline gap-1 sm:gap-1.5 min-w-0">
        <span className="text-sm font-semibold tabular-nums">{value.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground truncate">{label}</span>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'running':
      return (
        <Badge
          variant="secondary"
          className="gap-1.5 bg-blue-500/10 text-blue-600 border-blue-200 dark:border-blue-800"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          Em andamento
        </Badge>
      )
    case 'completed':
      return (
        <Badge
          variant="secondary"
          className="gap-1.5 bg-green-500/10 text-green-600 border-green-200 dark:border-green-800"
        >
          <CheckCircle2 className="h-3 w-3" />
          Concluído
        </Badge>
      )
    case 'failed':
      return (
        <Badge
          variant="secondary"
          className="gap-1.5 bg-destructive/10 text-destructive border-destructive/20"
        >
          <XCircle className="h-3 w-3" />
          Falhou
        </Badge>
      )
    default:
      return null
  }
}
